import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_API_URL;

export const listClients = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients`, {
    params,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // { items, page, pageSize, totalItems, totalPages }
};

export const listContacts = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/contacts`, {
    params,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // { items, page, pageSize, totalItems, totalPages }
};

export const getClient = async (clientId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/${clientId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const createClient = async (payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/clients`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const updateClient = async (clientId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(`${BASE_URL}/clients/${clientId}`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const deleteClient = async (clientId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(`${BASE_URL}/clients/${clientId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // { message: "CLIENT_DELETED" }
};

// Counts of dependents an irreversible cascade delete would remove. Powers
// the delete confirmation modal's impact summary.
export const getClientDeleteImpact = async (clientId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/${clientId}/delete-impact`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // { clientId, clientName, impact: { needs, matches, contacts, comments } }
};

// Hard delete a company and all of its dependents. The backend requires the
// caller to echo the exact phrase "delete all permanently" to acknowledge the
// irreversible cascade.
export const confirmDeleteClient = async (clientId, confirmation, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/clients/${clientId}/confirm-delete`,
    { confirmation },
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// Summary of all companies awaiting an Excel-initiated delete approval in the
// current BU (or whole company for a Company Admin with no BU selected). Powers
// the "approve all deletions" button visibility and its confirmation screen.
export const getPendingDeleteSummary = async (options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/pending-delete/summary`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // { clientCount, impact: { needs, matches, contacts, comments } }
};

// Approve (hard delete) every pending company deletion at once. Like the
// single-client confirm, the backend requires the exact irreversible-cascade
// phrase to be echoed back.
export const confirmAllPendingDeletes = async (confirmation, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/clients/pending-delete/confirm`,
    { confirmation },
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data; // { message: "PENDING_DELETIONS_CONFIRMED", deleted }
};

export const importClients = async (file, defaultManagerId, options = {}) => {
  const { buId } = options || {};
  const formData = new FormData();
  formData.append("file", file);
  const params = {};
  if (defaultManagerId) params.defaultManagerId = defaultManagerId;
  const response = await axios.post(`${BASE_URL}/clients/import`, formData, {
    headers: { "Content-Type": "multipart/form-data", ...(buId ? { "X-Business-Unit-Id": buId } : {}) },
    params
  });
  return response.data; // returns created items with pagination metadata
};

export const getImportTemplate = async (options = {}) => {
  const { buId, lang } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/import/template`, {
    responseType: "blob",
    params: lang ? { lang } : undefined,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response;
};

export const importWorkbook = async (file, defaultManagerEmail, options = {}) => {
  const { buId } = options || {};
  const formData = new FormData();
  formData.append("file", file);
  const params = {};
  if (defaultManagerEmail) params.defaultManagerEmail = defaultManagerEmail;
  const response = await axios.post(`${BASE_URL}/clients/import/workbook`, formData, {
    headers: { "Content-Type": "multipart/form-data", ...(buId ? { "X-Business-Unit-Id": buId } : {}) },
    params
  });
  return response.data; // { company: {...}, client: {...}, need: {...}, consultant: {...} }
};

// Create a contact without a parent company — backend attaches it to the
// per-BU "Unknown" placeholder so it still appears on the contact kanban.
export const createContactWithoutCompany = async (payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/clients/contacts/unknown`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const listUsersInMyBusinessUnit = async (options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/auth/users/in-my-business-unit`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // array of { id, email, first_name, last_name, role }
};

export const blacklistClient = async (clientId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/clients/${clientId}/blacklist`, null, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const unblacklistClient = async (clientId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/clients/${clientId}/unblacklist`, null, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const exportClients = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/export`, {
    params,
    responseType: "blob",
    headers: {
      Accept: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ...(buId ? { "X-Business-Unit-Id": buId } : {})
    }
  });
  return response;
};

// Client Needs list for a specific client
export const listClientNeeds = async (params = {}, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/client-needs`, {
    params,
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // { items, page, pageSize, totalItems, totalPages }
};

// Client Contacts CRUD operations
export const listClientContacts = async (clientId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/${clientId}/contacts`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data; // array of contacts
};

export const getClientContact = async (clientId, contactId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/${clientId}/contacts/${contactId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const createClientContact = async (clientId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.post(`${BASE_URL}/clients/${clientId}/contacts`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const updateClientContact = async (clientId, contactId, payload, options = {}) => {
  const { buId } = options || {};
  const response = await axios.patch(`${BASE_URL}/clients/${clientId}/contacts/${contactId}`, payload, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

export const deleteClientContact = async (clientId, contactId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.delete(`${BASE_URL}/clients/${clientId}/contacts/${contactId}`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};

// Add a comment to a client
// POST /clients/{client_id}/comments body: { comment: "...", parentCommentId?: number|string }
export const addClientComment = async (clientId, comment, options = {}, parentCommentId) => {
  const { buId } = options || {};
  const response = await axios.post(
    `${BASE_URL}/clients/${clientId}/comments`,
    { comment, ...(parentCommentId ? { parentCommentId } : {}) },
    {
      ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
    }
  );
  return response.data;
};

// Get comments for a client (thread tree)
// GET /clients/{client_id}/comments
export const getClientComments = async (clientId, options = {}) => {
  const { buId } = options || {};
  const response = await axios.get(`${BASE_URL}/clients/${clientId}/comments`, {
    ...(buId ? { headers: { "X-Business-Unit-Id": buId } } : {})
  });
  return response.data;
};


